import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // App basics
    appTitle: "CruzaSmart",
    tagline: "Real-time border wait reports. Powered by the people.",
    
    // Navigation
    checkIn: "Check In",
    checkOut: "Check Out",
    borderCrossings: "Border Crossings",
    featuredCrossing: "Featured",
    proTip: "Pro Tip",
    tapToLearnMore: "Learn More",
    earnedXP: "+3 XP earned!",
    thanksForExploring: "Thanks for checking this out!",
    sponsoredBy: "Sponsored by",
    learnMore: "Learn More",
    by: "by",
    home: "Home",
    rewards: "Rewards",
    profile: "Profile",
    reports: "Chat",
    
    // Banner messages
    bannerWaitTimes: "Wait times update every minute",
    bannerEarnPoints: "Check in today to keep your streak!",
    
    // Points and rewards
    points: "Points",
    earnPoints: "+10 points for each time you mark your entry and exit",
    redeem: "Redeem",
    seeDetails: "See Details",
    redeemReward: "Redeem Reward",
    redeemSuccess: "Reward Redeemed!",
    redeemError: "Redemption Failed",
    notEnoughPoints: "Not enough points",
    needMorePoints: "Need More",
    available: "Available",
    pointsRequired: "points required",
    
    // Language
    selectLanguage: "Select Language",
    english: "English", 
    spanish: "Spanish",
    
    // Border crossing
    borderWaitTimes: "Border Wait Times",
    recentReports: "Recent Reports",
    noReports: "No reports available",
    reportedMinutesAgo: "minutes ago",
    atGarita: "at",
    lastReported: "Last reported",
    currentWait: "Current wait",
    aiPrediction: "AI Prediction",
    
    // Lane types
    readyLane: "Ready Lane",
    normal: "Normal",
    sentri: "SENTRI",
    general: "General",
    pedestrian: "Pedestrian",
    vehicle: "Vehicle",
    
    // Check-in process
    selectBorder: "Select Border Crossing",
    selectMode: "How are you crossing?",
    selectLane: "Which lane?",
    takePhoto: "Take Photo",
    addPhoto: "Add Photo",
    photoOptional: "Photo (Optional)",
    helpCommunity: "Help the Community",
    photoGuidelines: "Guidelines: No faces or personal info",
    startTimer: "Start Timer & Check In",
    completeFields: "Complete all fields to check in",
    checkedIn: "Checked In!",
    timerRunning: "Timer running - safe travels!",
    waitTime: "Wait Time",
    tripDetails: "Trip Details",
    border: "Border",
    lane: "Lane",
    travel: "Travel",
    car: "Car",
    
    // Profile and auth
    welcome: "Welcome",
    signIn: "Sign In",
    signUp: "Sign Up",
    signOut: "Sign Out",
    createAccount: "Create Account",
    fullName: "Full Name",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    phone: "Phone",
    phoneOptional: "Phone (Optional)",
    personalInfo: "Personal Information",
    editProfile: "Edit Profile",
    saveChanges: "Save Changes",
    memberSince: "Member since",
    lastLogin: "Last login",
    achievements: "Achievements",
    totalReports: "Total Reports",
    helpfulVotes: "Helpful Votes",
    accuracy: "Accuracy",
    recentActivity: "Recent Activity",
    
    // Status and errors
    online: "Online",
    offline: "Offline",
    loading: "Loading",
    error: "Error",
    success: "Success",
    failed: "Failed",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    confirm: "Confirm",
    
    // Rewards
    rewardsTitle: "Rewards",
    rewardSubtitle: "Redeem your points",
    freeCoffee: "Free Coffee",
    coffeeDesc: "Free coffee at participating cafes",
    fastFood: "Fast Food Combo",
    fastFoodDesc: "Free combo meal at border restaurants",
    gasDiscount: "Gas Discount",
    gasDesc: "$5 off gas at participating stations",
    shoppingVoucher: "Shopping Voucher",
    shoppingDesc: "$10 shopping voucher",
    restaurantMeal: "Restaurant Meal",
    restaurantDesc: "Free meal at premium restaurants",
    
    // Connection status
    connectionOnline: "Back online!",
    connectionOffline: "You're offline",
    syncingData: "Syncing your offline data...",
    offlineMode: "Your data will sync when connection returns",
    dataWillSync: "Data will sync when connected",
    
    // Auth errors
    invalidCredentials: "Invalid email or password",
    userExists: "User already registered",
    weakPassword: "Password must be at least 6 characters",
    passwordMismatch: "Passwords do not match",
    signInRequired: "Sign in required",
    authRequired: "Authentication required",
    
    // Border crossings
    "San Ysidro (Southbound to Tijuana)": "San Ysidro (Southbound to Tijuana)",
    "Otay (Southbound to Tijuana)": "Otay (Southbound to Tijuana)",

    // Onboarding screens
    screen1Title: "Real-Time Wait Times",
    screen1Desc: "See live border crossing wait times reported by travelers like you. Save hours of waiting.",
    screen2Title: "1-Tap Check-In",
    screen2Desc: "Check in when you arrive at the border. We'll track your wait time automatically.",
    screen3Title: "Earn Points & Rewards",
    screen3Desc: "Help the community and earn points. Redeem for rewards from local businesses!",
    skip: "Skip",
    next: "Next",
    letsGo: "Get Started",
    back: "Back",
    
    // Support page translations
    support: "Support",
    helpCenter: "Help Center",
    faq: "Frequently Asked Questions",
    contactUs: "Contact Us",
    howToGuides: "How-To Guides",
    troubleshooting: "Troubleshooting",
    accountHelp: "Account Help",
    communityGuidelines: "Community Guidelines",
    emergencyContact: "Emergency Contact",
    supportEmail: "Email us at support@cruzasmart.com",
    supportPhone: "Call us: +1 (555) 123-4567",
    
    // Report problem translations
    reportProblem: "Report a Problem",
    reportProblemDesc: "Let us know about any issues",
    selectIssueCategory: "Select Issue Category",
    bugTechnical: "Bug or Technical Issue",
    incorrectWaitTime: "Incorrect Wait Time",
    inappropriatePhoto: "Inappropriate Photo",
    wrongBorderInfo: "Wrong Border/Lane Info",
    featureRequest: "Feature Request",
    otherIssue: "Other Issue",
    describeIssue: "Describe the issue",
    attachScreenshot: "Attach Screenshot (Optional)",
    submitReport: "Submit Report",
    reportSubmitted: "Problem Reported!",
    reportSubmittedDesc: "We'll investigate and get back to you.",
    reportError: "Error Submitting Report",
    myReports: "My Reports",
    reportStatus: "Status",
    statusOpen: "Open",
    statusInvestigating: "Investigating",
    statusResolved: "Resolved",
    faqQuestion1: "How do I check in and check out?",
    faqAnswer1: "Tap the 'Check In' button when you join the border line, then 'Check Out' when you complete your crossing. This helps us track accurate wait times.",
    faqQuestion2: "How do I earn and redeem points?",
    faqAnswer2: "Earn points by checking in (+5), checking out (+5), and uploading photos (+10). Redeem them in the Rewards section for discounts and prizes.",
    faqQuestion3: "How accurate are the wait times?",
    faqAnswer3: "Wait times are based on real user reports and AI predictions. The more people use the app, the more accurate our estimates become.",
    faqQuestion4: "What should I do if the app isn't working?",
    faqAnswer4: "Try restarting the app, checking your internet connection, or updating to the latest version. Contact support if problems persist.",
    guideCheckinOut: "How to Check In and Out",
    guideCheckinOutSteps: "1. Tap 'Check In' when joining the line\n2. Keep the app open to track your wait\n3. Tap 'Check Out' when you cross\n4. Optionally add a photo to help others",
    guidePoints: "How to Earn Points",
    guidePointsSteps: "• Check In: +5 points\n• Check Out: +5 points\n• Upload Photo: +10 points\n• Accurate Reports: Bonus points\n• Weekly streaks: Extra rewards",
    guideReports: "How to Submit Accurate Reports",
    guideReportsSteps: "• Use the app from the actual border line\n• Take photos showing current conditions\n• Be honest about wait times\n• Include helpful details in comments",
    accountIssues: "Common Account Issues",
    accountIssuesText: "• Forgot password: Use 'Reset Password' on login\n• Email not verified: Check spam folder\n• Account deletion: Contact support\n• Privacy concerns: Review our Privacy Policy",
    communityRules: "Community Rules",
    communityRulesText: "• Be respectful to all users\n• Submit accurate, helpful reports\n• No inappropriate photos\n• No spam or advertising\n• Follow all border crossing laws",
    technicalIssues: "Technical Issues",
    technicalIssuesText: "• App won't load: Check internet connection\n• GPS not working: Enable location services\n• Photos won't upload: Check storage space\n• Crashes: Update to latest version",
    emergencyInfo: "For urgent border-related emergencies, contact CBP at (877) 227-5511 or Mexican authorities at 911."
  },
  es: {
    // App basics
    appTitle: "CruzaSmart",
    tagline: "Reportes de espera en tiempo real. Impulsado por la gente.",
    
    // Navigation
    checkIn: "Registrarse",
    checkOut: "Salir",
    borderCrossings: "Cruces Fronterizos",
    featuredCrossing: "Destacado",
    proTip: "Consejo",
    tapToLearnMore: "Más información",
    earnedXP: "¡+3 XP ganados!",
    thanksForExploring: "¡Gracias por ver esto!",
    sponsoredBy: "Patrocinado por",
    learnMore: "Más información",
    by: "por",
    home: "Inicio",
    rewards: "Recompensas",
    profile: "Perfil",
    reports: "Chat",
    
    // Banner messages
    bannerWaitTimes: "Los tiempos de espera se actualizan cada minuto",
    bannerEarnPoints: "¡Regístrate hoy para mantener tu racha!",
    
    // Border crossings
    "San Ysidro (Southbound to Tijuana)": "San Ysidro (Salida a Tijuana)",
    "Otay (Southbound to Tijuana)": "Otay (Salida a Tijuana)",
    
    // Points and rewards
    points: "Puntos",
    earnPoints: "+10 puntos por cada vez que marcas tu entrada y salida",
    redeem: "Canjear",
    seeDetails: "Ver Detalles",
    redeemReward: "Canjear Recompensa",
    redeemSuccess: "¡Recompensa Canjeada!",
    redeemError: "Error al Canjear",
    notEnoughPoints: "Puntos insuficientes",
    needMorePoints: "Necesitas Más",
    available: "Disponible",
    pointsRequired: "puntos requeridos",
    
    // Language
    selectLanguage: "Seleccionar Idioma",
    english: "Inglés",
    spanish: "Español",
    
    // Border crossing
    borderWaitTimes: "Tiempos de Espera en la Frontera",
    recentReports: "Reportes Recientes",
    noReports: "No hay reportes disponibles",
    reportedMinutesAgo: "minutos atrás",
    atGarita: "en",
    lastReported: "Último reporte",
    currentWait: "Espera actual",
    aiPrediction: "Predicción IA",
    
    // Lane types
    readyLane: "Carril Ready",
    normal: "Normal",
    sentri: "SENTRI",
    general: "General",
    pedestrian: "Peatonal",
    vehicle: "Vehículo",
    
    // Check-in process
    selectBorder: "Selecciona el Cruce Fronterizo",
    selectMode: "¿Cómo vas a cruzar?",
    selectLane: "¿Qué carril?",
    takePhoto: "Tomar Foto",
    addPhoto: "Agregar Foto",
    photoOptional: "Foto (Opcional)",
    helpCommunity: "Ayuda a la Comunidad",
    photoGuidelines: "Instrucciones: Sin caras o información personal",
    startTimer: "Iniciar Cronómetro y Registrarse",
    completeFields: "Completa todos los campos para registrarte",
    checkedIn: "¡Registrado!",
    timerRunning: "Cronómetro corriendo - ¡buen viaje!",
    waitTime: "Tiempo de Espera",
    tripDetails: "Detalles del Viaje",
    border: "Frontera",
    lane: "Carril",
    travel: "Viaje",
    car: "Auto",
    
    // Profile and auth
    welcome: "Bienvenido",
    signIn: "Iniciar Sesión",
    signUp: "Crear Cuenta",
    signOut: "Cerrar Sesión",
    createAccount: "Crear Cuenta",
    fullName: "Nombre Completo",
    email: "Correo Electrónico",
    password: "Contraseña",
    confirmPassword: "Confirmar Contraseña",
    phone: "Teléfono",
    phoneOptional: "Teléfono (Opcional)",
    personalInfo: "Información Personal",
    editProfile: "Editar Perfil",
    saveChanges: "Guardar Cambios",
    memberSince: "Miembro desde",
    lastLogin: "Última conexión",
    achievements: "Logros",
    totalReports: "Reportes Totales",
    helpfulVotes: "Votos Útiles",
    accuracy: "Precisión",
    recentActivity: "Actividad Reciente",
    
    // Status and errors
    online: "En línea",
    offline: "Sin conexión",
    loading: "Cargando",
    error: "Error",
    success: "Éxito",
    failed: "Falló",
    retry: "Reintentar",
    cancel: "Cancelar",
    save: "Guardar",
    edit: "Editar",
    delete: "Eliminar",
    confirm: "Confirmar",
    
    // Rewards
    rewardsTitle: "Recompensas",
    rewardSubtitle: "Canjea tus puntos",
    freeCoffee: "Café Gratis",
    coffeeDesc: "Café gratis en cafeterías participantes",
    fastFood: "Combo de Comida Rápida",
    fastFoodDesc: "Combo gratis en restaurantes fronterizos",
    gasDiscount: "Descuento en Gasolina",
    gasDesc: "$5 de descuento en gasolineras participantes",
    shoppingVoucher: "Cupón de Compras",
    shoppingDesc: "Cupón de $10 para compras",
    restaurantMeal: "Comida de Restaurante",
    restaurantDesc: "Comida gratis en restaurantes premium",
    
    // Connection status
    connectionOnline: "¡De vuelta en línea!",
    connectionOffline: "Estás sin conexión",
    syncingData: "Sincronizando tus datos sin conexión...",
    offlineMode: "Tus datos se sincronizarán cuando regrese la conexión",
    dataWillSync: "Los datos se sincronizarán cuando esté conectado",
    
    // Auth errors
    invalidCredentials: "Email o contraseña inválidos",
    userExists: "Usuario ya registrado",
    weakPassword: "La contraseña debe tener al menos 6 caracteres",
    passwordMismatch: "Las contraseñas no coinciden",
    signInRequired: "Inicio de sesión requerido",
    authRequired: "Autenticación requerida",

    // Onboarding screens
    screen1Title: "Tiempos de Espera en Tiempo Real",
    screen1Desc: "Ve los tiempos de espera en tiempo real reportados por viajeros como tú. Ahorra horas de espera.",
    screen2Title: "Registro en 1 Toque",
    screen2Desc: "Regístrate cuando llegues a la frontera. Rastrearemos tu tiempo de espera automáticamente.",
    screen3Title: "Gana Puntos y Recompensas",
    screen3Desc: "Ayuda a la comunidad y gana puntos. ¡Canjéalos por recompensas de negocios locales!",
    skip: "Saltar",
    next: "Siguiente",
    letsGo: "Comenzar",
    back: "Atrás",
    
    // Support page translations
    support: "Soporte",
    helpCenter: "Centro de Ayuda",
    faq: "Preguntas Frecuentes",
    contactUs: "Contáctanos",
    howToGuides: "Guías de Uso",
    troubleshooting: "Solución de Problemas",
    accountHelp: "Ayuda con la Cuenta",
    communityGuidelines: "Lineamientos de la Comunidad",
    emergencyContact: "Contacto de Emergencia",
    supportEmail: "Escríbenos a support@cruzasmart.com",
    supportPhone: "Llámanos: +1 (555) 123-4567",
    
    // Report problem translations
    reportProblem: "Reportar un Problema",
    reportProblemDesc: "Cuéntanos sobre cualquier problema",
    selectIssueCategory: "Selecciona la Categoría",
    bugTechnical: "Error Técnico",
    incorrectWaitTime: "Tiempo de Espera Incorrecto",
    inappropriatePhoto: "Foto Inapropiada",
    wrongBorderInfo: "Información de Cruce Incorrecta",
    featureRequest: "Solicitud de Función",
    otherIssue: "Otro Problema",
    describeIssue: "Describe el problema",
    attachScreenshot: "Adjuntar Captura (Opcional)",
    submitReport: "Enviar Reporte",
    reportSubmitted: "¡Problema Reportado!",
    reportSubmittedDesc: "Investigaremos y te contactaremos.",
    reportError: "Error al Enviar Reporte",
    myReports: "Mis Reportes",
    reportStatus: "Estado",
    statusOpen: "Abierto",
    statusInvestigating: "Investigando",
    statusResolved: "Resuelto",
    faqQuestion1: "¿Cómo me registro y salgo del sistema?",
    faqAnswer1: "Presiona 'Registrarse' cuando te unas a la fila fronteriza, luego 'Salir' cuando completes tu cruce. Esto nos ayuda a rastrear tiempos de espera precisos.",
    faqQuestion2: "¿Cómo gano y uso mis puntos?",
    faqAnswer2: "Gana puntos registrándote (+5), saliendo (+5), y subiendo fotos (+10). Canjéalos en la sección de Recompensas por descuentos y premios.",
    faqQuestion3: "¿Qué tan precisos son los tiempos de espera?",
    faqAnswer3: "Los tiempos de espera se basan en reportes reales de usuarios y predicciones de IA. Mientras más gente use la app, más precisas se vuelven nuestras estimaciones.",
    faqQuestion4: "¿Qué debo hacer si la app no funciona?",
    faqAnswer4: "Intenta reiniciar la app, verificar tu conexión a internet, o actualizar a la última versión. Contacta soporte si los problemas persisten.",
    guideCheckinOut: "Cómo Registrarse y Salir",
    guideCheckinOutSteps: "1. Presiona 'Registrarse' al unirte a la fila\n2. Mantén la app abierta para rastrear tu espera\n3. Presiona 'Salir' cuando cruces\n4. Opcionalmente agrega una foto para ayudar a otros",
    guidePoints: "Cómo Ganar Puntos",
    guidePointsSteps: "• Registrarse: +5 puntos\n• Salir: +5 puntos\n• Subir Foto: +10 puntos\n• Reportes Precisos: Puntos bonus\n• Rachas semanales: Recompensas extra",
    guideReports: "Cómo Enviar Reportes Precisos",
    guideReportsSteps: "• Usa la app desde la fila fronteriza real\n• Toma fotos mostrando condiciones actuales\n• Sé honesto sobre tiempos de espera\n• Incluye detalles útiles en comentarios",
    accountIssues: "Problemas Comunes de Cuenta",
    accountIssuesText: "• Olvidé contraseña: Usa 'Restablecer Contraseña' en login\n• Email no verificado: Revisa carpeta de spam\n• Eliminar cuenta: Contacta soporte\n• Preocupaciones de privacidad: Revisa nuestra Política de Privacidad",
    communityRules: "Reglas de la Comunidad",
    communityRulesText: "• Sé respetuoso con todos los usuarios\n• Envía reportes precisos y útiles\n• No fotos inapropiadas\n• No spam o publicidad\n• Sigue todas las leyes de cruce fronterizo",
    technicalIssues: "Problemas Técnicos",
    technicalIssuesText: "• App no carga: Verifica conexión a internet\n• GPS no funciona: Habilita servicios de ubicación\n• Fotos no suben: Verifica espacio de almacenamiento\n• Se cierra: Actualiza a la última versión",
    emergencyInfo: "Para emergencias fronterizas urgentes, contacta CBP al (877) 227-5511 o autoridades mexicanas al 911."
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'cruzasmart_language';

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'es') return stored;
    }
    return 'es';
  });

  useEffect(() => {
    const resolveLanguage = async () => {
      const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;

      // Honor an explicit choice made on this device.
      if (stored === 'en' || stored === 'es') {
        setLanguageState(stored);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: userData } = await supabase
          .from('users')
          .select('language_preference')
          .eq('id', session.user.id)
          .single();

        if (userData?.language_preference) {
          setLanguageState(userData.language_preference as Language);
          return;
        }
      }

      // Spanish remains the default; English is always available via the language toggle.
      setLanguageState('es');
    };

    resolveLanguage();
  }, []);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
    }

    
    // Save to user profile if authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase
        .from('users')
        .update({ language_preference: lang })
        .eq('id', session.user.id);
    }
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};